import axios from 'axios';

const REPORT_PATH = '/api/ClientDiagnostics/Report';
const MAX_PER_MINUTE = 10;
const MAX_PER_SESSION = 50;
const WINDOW_MS = 60000;
const CONTEXT_KEYS = 'currentTime,totalDuration,lastAcceptedTime,maxWatchedTime,readyState,networkState,paused,seeking,playbackRate,bufferedEnd,visibility,online,isIOS,isWechat,errorCode,retryCount,studyAttempt,events,method,status,endpoint,component,event'.split(',');
const SESSION_ID = 's-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
let sentCount = 0;
let timestamps = [];
let fingerprints = [];
let installed = false;
let currentRoute = null;

function safePath(value) {
  const input = String(value || '').split(/[?#]/)[0];
  try {
    if (/^(https?:)?\/\//i.test(input)) {
      const parsed = new URL(input, window.location && window.location.origin);
      return parsed.origin + parsed.pathname;
    }
  } catch (ignored) {}
  return input
    .replace(/^(https?:\/\/)[^/]*@/i, '$1')
    .replace(/^(\/\/)[^/]*@/, '$1');
}

function sanitize(value, limit) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value).slice(0, 20000)
    .replace(/(?:https?:)?\/\/[^\s<>"')]+/gi, match => safePath(match))
    .replace(/\/[\w./%:-]+[?#][^\s<>"')]+/g, match => safePath(match))
    .replace(/\b(?:authorization|proxy-authorization|cookie|set-cookie)\b["']?\s*[:=]\s*[^\r\n]*/gi, '[redacted header]')
    .replace(/\b(?:Bearer|Basic)\s+[^\s,;}]+/gi, '[redacted credential]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[redacted]')
    .replace(/\b(?:openid|open_id|unionid|password|passwd|pwd|authorization|access[_-]?token|refresh[_-]?token|token|signature|secret|credential|cookie|phone|mobile|email)\b["']?\s*[:=]\s*(?:"[^"]*"|'[^']*'|[^\s,;}]+)/gi, '[redacted]')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted]')
    .replace(/\+?\d[\d ()-]{7,}\d/g, '[redacted]')
    .slice(0, limit);
}

function cleanContext(source) {
  const result = {};
  if (!source || typeof source !== 'object') return result;
  CONTEXT_KEYS.forEach(key => {
    const value = source[key];
    if (key === 'events') {
      if (!Array.isArray(value)) return;
      result.events = value.slice(-20).map(item => {
        if (!item || typeof item !== 'object') return null;
        const entry = { event: sanitize(item.event, 80) };
        ['time', 'at', 'currentTime'].forEach(timeKey => {
          if (typeof item[timeKey] === 'number' && isFinite(item[timeKey])) entry[timeKey] = item[timeKey];
        });
        return entry;
      }).filter(Boolean);
    } else if (typeof value === 'number' && isFinite(value)) {
      result[key] = value;
    } else if (typeof value === 'boolean') {
      result[key] = value;
    } else if (typeof value === 'string') {
      result[key] = sanitize(key === 'endpoint' ? safePath(value) : value, 200);
    }
    if (JSON.stringify(result).length > 4000) delete result[key];
  });
  return result;
}

function utf8Size(value) {
  return encodeURIComponent(value).replace(/%[A-F\d]{2}/g, 'x').length;
}

/** Report a bounded anonymous diagnostic; failures must never affect application flow. */
export function reportClientIssue(issue) {
  try {
    if (!issue || typeof window === 'undefined' || !window.XMLHttpRequest) return false;
    const context = cleanContext(issue.context);
    if (/\/api\/ClientDiagnostics\/Report/i.test(context.endpoint || '')) return false;
    const now = Date.now();
    timestamps = timestamps.filter(time => now - time < WINDOW_MS);
    fingerprints = fingerprints.filter(entry => now - entry.time < WINDOW_MS);
    if (sentCount >= MAX_PER_SESSION || timestamps.length >= MAX_PER_MINUTE) return false;
    const payload = {
      kind: /^(video|js|promise|vue|network)$/.test(issue.kind) ? issue.kind : 'js',
      level: /^(warning|error|info)$/.test(issue.level) ? issue.level : 'error',
      message: sanitize(issue.message, 500),
      stack: sanitize(issue.stack, 3000),
      path: sanitize(safePath(issue.path || (currentRoute && currentRoute.currentRoute && currentRoute.currentRoute.path) || window.location.href), 500),
      userAgent: sanitize(window.navigator.userAgent, 500),
      sessionId: SESSION_ID,
      courseId: typeof issue.courseId === 'number' && issue.courseId >= 0 && issue.courseId <= 9007199254740991 && Math.floor(issue.courseId) === issue.courseId ? issue.courseId : null,
      occurredAt: new Date(now).toISOString(),
      context: context
    };
    payload.message = sanitize(payload.message, 500);
    payload.stack = sanitize(payload.stack, 3000);
    payload.path = safePath(payload.path).slice(0, 500);
    payload.userAgent = sanitize(payload.userAgent, 500);
    const fingerprint = JSON.stringify([payload.kind, payload.message, payload.path, payload.courseId, context.endpoint, context.status, context.event]);
    if (fingerprints.some(entry => entry.key === fingerprint)) return false;
    let body = JSON.stringify(payload);
    if (utf8Size(body) > 16384) {
      payload.stack = payload.stack.slice(0, 1000);
      payload.context = {};
      body = JSON.stringify(payload);
    }
    if (utf8Size(body) > 16384) return false;
    // Reserve the budget before sending, including failures, to prevent failure loops.
    timestamps.push(now);
    fingerprints.push({ key: fingerprint, time: now });
    sentCount += 1;
    const request = new window.XMLHttpRequest();
    const base = safePath(axios.defaults.baseURL || '').replace(/\/$/, '');
    request.open('POST', base + REPORT_PATH, true);
    request.timeout = 5000;
    request.withCredentials = false;
    request.setRequestHeader('Content-Type', 'application/json');
    request.onerror = request.ontimeout = request.onabort = function () {};
    request.send(body);
    return true;
  } catch (ignored) {
    return false;
  }
}

function reportError(kind, error, context, fallback) {
  try {
    reportClientIssue({
      kind: kind,
      message: error && typeof error.message === 'string' ? error.message : (typeof error === 'string' ? error : fallback || 'Unknown client error'),
      stack: error && error.stack,
      context: context
    });
  } catch (ignored) {}
}

/** Install global handlers once, retaining the existing Vue error behavior. */
export function installClientDiagnostics(Vue, router) {
  try {
    if (installed || typeof window === 'undefined') return;
    installed = true;
    currentRoute = router;
    const previous = Vue.config.errorHandler;
    Vue.config.errorHandler = function (error, vm, info) {
      reportError('vue', error, { component: vm && vm.$options && vm.$options.name, event: info });
      if (typeof previous === 'function') return previous.call(this, error, vm, info);
      if (window.console && window.console.error) window.console.error(error);
    };
    window.addEventListener('error', function (event) {
      reportError('js', event.error, { endpoint: event.filename }, event.message);
    });
    window.addEventListener('unhandledrejection', function (event) {
      reportError('promise', event.reason);
    });
    if (router && typeof router.onError === 'function') {
      router.onError(function (error) { reportError('js', error, { event: 'router' }); });
    }
  } catch (ignored) {}
}



// WEBPACK FOOTER //
// ./src/utils/clientDiagnostics.js